import { createSelector } from "reselect"

import { EmployeeHolidayRecord, EmployeePTORecord } from "../cached-data/types"
import { ReduxState, TkSummary } from "../common/types"

export const selectPTOObjects = (state: ReduxState): Record<number, EmployeePTORecord> =>
    state.entities.employeePTO?.objects ?? {}

export const selectHolidayObjects = (state: ReduxState): Record<string, EmployeeHolidayRecord> =>
    state.entities.employeeHolidays?.objects ?? {}

const ptoObjectsSelector = selectPTOObjects
const holidayObjectsSelector = selectHolidayObjects
const tkSummarySelector = (state: ReduxState) => state.exceptions.tkSummary

/**
 * Compute fulfillment for every PTO and holiday record, using the provided tkSummary as the
 * source of truth. Extracted from {@link selectPTOFulfillment} so callers that hold the raw
 * entity data (e.g. class components via `mapStateToProps`) can compute fulfillment without
 * reconstructing a Redux state object.
 *
 * Returns an empty object when `tkSummary` is null or undefined.
 *
 * Fulfillment rules:
 *   - **ABSENCE_TYPE**: a tkSummary row for the employee on the record's date has the
 *     record's `absence_type_id` in its `absence_type_ids` array.
 *   - **COST_CODE**: the sum of `st` minutes across rows for the employee on the date
 *     whose `cost_code.pto_bucket_id` matches `pto_bucket_id` is ≥ `hours × 60`.
 *     Records with null or zero `hours` are never fulfilled.
 *
 * @param ptoObjects - EmployeePTO records keyed by ID.
 * @param holidayObjects - EmployeeHoliday records keyed by ID.
 * @param tkSummary - TkSummary data, or null/undefined if not yet fetched.
 * @returns A map of record ID (string) → fulfilled boolean.
 */
export const computePTOFulfillment = (
    ptoObjects: Record<number, EmployeePTORecord>,
    holidayObjects: Record<string, EmployeeHolidayRecord>,
    tkSummary: TkSummary | null | undefined
): Record<string, boolean> => {
    if (!tkSummary) return {}

    const result: Record<string, boolean> = {}
    const allRecords: Array<EmployeePTORecord | EmployeeHolidayRecord> = [
        ...Object.values(ptoObjects),
        ...Object.values(holidayObjects),
    ]

    for (const record of allRecords) {
        const dateRows = (tkSummary[String(record.employee)] ?? []).filter(row => row.shift_date === record.date)

        let fulfilled = false
        if (record.pto_bucket_type === "ABSENCE_TYPE") {
            const absenceTypeId = record.absence_type_id
            if (absenceTypeId !== null) {
                fulfilled = dateRows.some(row => row.absence_type_ids?.includes(absenceTypeId))
            }
        } else if (record.pto_bucket_type === "COST_CODE") {
            if (record.hours !== null && record.hours > 0) {
                const totalSt = dateRows
                    .filter(row => row.cost_code?.pto_bucket_id === record.pto_bucket_id)
                    .reduce((sum, row) => sum + (row.st ?? 0), 0)
                fulfilled = totalSt / 60 >= record.hours
            }
        }

        result[String(record.id)] = fulfilled
    }

    return result
}

/**
 * Memoized selector that computes PTO/holiday fulfillment from the Redux store.
 *
 * @returns A map of record ID (string) → fulfilled boolean covering both PTO and
 *   holiday records. IDs do not overlap between the two entity types.
 */
export const selectPTOFulfillment = createSelector(
    ptoObjectsSelector,
    holidayObjectsSelector,
    tkSummarySelector,
    computePTOFulfillment
)
